/**
 * useLanguageSync — bridges the Dexie "#language" setting with i18next.
 *
 * Responsibilities:
 * 1. On mount: read "#language" from Dexie → call i18n.changeLanguage()
 * 2. On Dexie change: react to live-query updates → update i18next
 * 3. Mirror to localStorage for flicker-free startup on next page load
 * 4. Handle "system" value: resolve to navigator language
 *
 * Mount this hook once near the top of the component tree (e.g. AppLayout).
 */

import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import dayjs from "dayjs"
import { useSetting } from "@/features/settings/hooks/useSetting"
import { LANGUAGE_CACHE_KEY, SUPPORTED_LANGUAGES } from "@/i18n/i18n"
import type { SupportedLanguage } from "@/i18n/i18n"

/**
 * Resolve a language setting value to a concrete i18next language code.
 * - "system" → detect from navigator, fall back to "en"
 * - any supported code → pass through
 * - anything else → "en"
 */
function resolveLanguage(setting: string): SupportedLanguage {
  if (setting === "system") {
    const browser = (navigator.language || "en").split(
      "-",
    )[0] as SupportedLanguage
    return SUPPORTED_LANGUAGES.includes(browser) ? browser : "en"
  }
  return SUPPORTED_LANGUAGES.includes(setting as SupportedLanguage)
    ? (setting as SupportedLanguage)
    : "en"
}

/**
 * Sync dayjs global locale to match the resolved language.
 * Uses dynamic import so any of dayjs's 70+ locales work without
 * bundling them all. English is built-in and needs no import.
 */
async function syncDayjsLocale(lang: string): Promise<void> {
  if (lang === "en") {
    dayjs.locale("en")
    return
  }
  try {
    await import(/* @vite-ignore */ `dayjs/locale/${lang}.js`)
    dayjs.locale(lang)
  } catch {
    // Locale not available in dayjs — keep "en" as fallback
    dayjs.locale("en")
  }
}

export function useLanguageSync() {
  const { i18n } = useTranslation()
  const { value: languageSetting, isLoading } = useSetting(
    "#language",
    "system",
  )

  useEffect(() => {
    // While Dexie is still loading, the hook returns the default ("system").
    // Resolving "system" would use navigator language and override the
    // localStorage-cached value that LanguageDetector already picked up —
    // causing a flash (e.g. sv → en). Skip until Dexie has loaded.
    if (isLoading) return

    const resolved = resolveLanguage(languageSetting)

    // Update i18next if needed
    if (i18n.language !== resolved) {
      void i18n.changeLanguage(resolved)
    }

    // Sync dayjs locale (for date formatting: "dddd", "MMMM", etc.)
    void syncDayjsLocale(resolved)

    // Mirror to localStorage for flicker-free startup
    try {
      localStorage.setItem(LANGUAGE_CACHE_KEY, resolved)
    } catch {
      // localStorage unavailable — ignore
    }
  }, [languageSetting, isLoading, i18n])
}
